import { Alert, Stack } from '@mui/material'
import type { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'

import { BidType } from '@/features/bidding/constants'
import type { CapacityGateTime, CapacityMarketFamily } from '@/features/bidding/utils/importOfferBiddingWindow'
import {
  FCR_CAPACITY_GATE,
  formatTimeRemaining,
  getCapacityWindow,
  MFRR_CAPACITY_GATE,
} from '@/features/bidding/utils/importOfferBiddingWindow'

const CAPACITY_MARKET_WINDOWS = [
  {
    testId: 'fcr-capacity-window',
    family: 'FCR',
    openKey: 'bidding.import_energy_offer.windows.fcr_capacity_open',
    closedKey: 'bidding.import_energy_offer.windows.fcr_capacity_closed',
    gate: FCR_CAPACITY_GATE,
  },
  {
    testId: 'mfrr-capacity-window',
    family: 'MFRR',
    openKey: 'bidding.import_energy_offer.windows.mfrr_capacity_open',
    closedKey: 'bidding.import_energy_offer.windows.mfrr_capacity_closed',
    gate: MFRR_CAPACITY_GATE,
  },
] as const satisfies readonly {
  testId: string
  family: CapacityMarketFamily
  openKey: string
  closedKey: string
  gate: CapacityGateTime
}[]

type BiddingWindowsPanelProps = {
  now: DateTime
  bidType: BidType
  // The market family the pasted TSV resolved to, if any — its row is emphasized, the other dimmed
  highlightedMarket?: CapacityMarketFamily
}

function BiddingWindowsPanel({ now, bidType, highlightedMarket }: Readonly<BiddingWindowsPanelProps>) {
  const { t } = useTranslation()

  if (bidType === BidType.ENERGY) {
    return (
      <Stack data-testid="bidding-windows-panel" spacing={1}>
        <Alert data-testid="mfrr-energy-window" severity="info">
          {t('bidding.import_energy_offer.windows.mfrr_energy')}
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack data-testid="bidding-windows-panel" spacing={1}>
      {CAPACITY_MARKET_WINDOWS.map(({ testId, family, openKey, closedKey, gate }) => {
        const window = getCapacityWindow(now, gate)
        const isHighlighted = highlightedMarket === family
        const severityColor = window.isClosed ? 'warning.main' : 'info.main'
        let sx
        if (isHighlighted) {
          sx = { border: 2, borderColor: severityColor }
        } else if (highlightedMarket !== undefined) {
          sx = { opacity: 0.45 }
        }
        return window.isClosed ? (
          <Alert
            key={testId}
            data-highlighted={highlightedMarket === family}
            data-testid={testId}
            severity="warning"
            sx={sx}
          >
            {t(closedKey, {
              deliveryDay: window.deliveryDay,
              time: window.closesAt.toFormat('HH:mm ZZZZ'),
              nextDeliveryDay: window.nextDeliveryDay,
              nextTime: window.nextClosesAt.toFormat('MMM d, HH:mm ZZZZ'),
            })}
          </Alert>
        ) : (
          <Alert
            key={testId}
            data-highlighted={highlightedMarket === family}
            data-testid={testId}
            severity="info"
            sx={sx}
          >
            {t(openKey, {
              deliveryDay: window.deliveryDay,
              time: window.closesAt.toFormat('HH:mm ZZZZ'),
              remaining: formatTimeRemaining(now, window.closesAt),
            })}
          </Alert>
        )
      })}
    </Stack>
  )
}

export default BiddingWindowsPanel
